/**
 * Recipe search — in-place results.
 * Works for both the bar section on the blog page (sections/recipe-search.liquid) and the
 * results page (sections/main-recipe-search.liquid). On submit it fetches
 *   /search?view=recipes&type=article&q=…&section_id=<results section>
 * via the Section Rendering API, drops the rendered results into the root's results container,
 * intercepts pagination links inside it, keeps ?q= / ?page= in the URL, and — on the blog page —
 * hides the blog's own listing via body.recipe-search-active. Without JS the form submits.
 */
(function () {
  const ACTIVE_CLASS = "recipe-search-active";
  const LOADING_CLASS = "recipe-search--loading";

  document.querySelectorAll("[data-recipe-search]").forEach((root) => {
    const form = root.querySelector("[data-recipe-search-form]");
    const input = root.querySelector("[data-recipe-search-input]");
    const clearBtn = root.querySelector("[data-recipe-search-clear]");
    const results = root.querySelector("[data-recipe-search-results]");
    if (!form || !input || !results) return;

    const searchUrl = root.dataset.searchUrl || "/search";
    const sectionId = root.dataset.resultsSection || "main-recipe-search";
    const isPage = root.classList.contains("recipe-search--page");
    let controller = null;

    const setLoading = (on) => {
      root.classList.toggle(LOADING_CLASS, on);
      results.setAttribute("aria-busy", on ? "true" : "false");
    };

    const setUrl = (q, page) => {
      const url = new URL(window.location.href);
      if (q) url.searchParams.set("q", q);
      else url.searchParams.delete("q");
      if (page && page > 1) url.searchParams.set("page", String(page));
      else url.searchParams.delete("page");
      if (isPage) {
        url.searchParams.set("view", "recipes");
        url.searchParams.set("type", "article");
      }
      history.replaceState(history.state, "", url.toString());
    };

    const buildUrl = (q, page) => {
      const params = new URLSearchParams({
        view: "recipes",
        type: "article",
        "options[prefix]": "last",
        q: q,
        section_id: sectionId,
      });
      if (page && page > 1) params.set("page", String(page));
      return searchUrl + "?" + params.toString();
    };

    const show = (html) => {
      const doc = new DOMParser().parseFromString(html, "text/html");
      const fresh = doc.querySelector("[data-recipe-search-results]");
      results.innerHTML = fresh ? fresh.innerHTML : "";
      results.hidden = false;
      document.body.classList.add(ACTIVE_CLASS);
      if (clearBtn) clearBtn.hidden = false;
    };

    const run = (q, page) => {
      if (controller) controller.abort();
      controller = new AbortController();
      setLoading(true);
      fetch(buildUrl(q, page), { signal: controller.signal })
        .then((r) => (r.ok ? r.text() : Promise.reject(r.status)))
        .then((html) => {
          show(html);
          setUrl(q, page);
        })
        .catch((err) => {
          if (err && err.name === "AbortError") return;
          // Fall back to a full navigation so the user still gets results.
          form.submit();
        })
        .finally(() => setLoading(false));
    };

    const clear = () => {
      if (controller) controller.abort();
      input.value = "";
      results.innerHTML = "";
      results.hidden = true;
      document.body.classList.remove(ACTIVE_CLASS);
      if (clearBtn) clearBtn.hidden = true;
      setLoading(false);
      if (isPage) {
        // Nothing to fall back to on the results page itself.
        input.focus();
        return;
      }
      setUrl("", 1);
      input.focus();
    };

    form.addEventListener("submit", (event) => {
      const q = input.value.trim();
      if (!q) {
        event.preventDefault();
        clear();
        return;
      }
      event.preventDefault();
      run(q, 1);
    });

    input.addEventListener("input", () => {
      if (clearBtn) clearBtn.hidden = input.value.trim() === "";
    });

    if (clearBtn) clearBtn.addEventListener("click", clear);

    // Pagination inside the results: fetch the page instead of navigating.
    results.addEventListener("click", (event) => {
      const link = event.target.closest("[data-recipe-search-pagination] a[href]");
      if (!link) return;
      const href = new URL(link.getAttribute("href"), window.location.origin);
      const q = input.value.trim();
      const page = parseInt(href.searchParams.get("page") || "1", 10);
      if (!q) return;
      event.preventDefault();
      run(q, page);
      root.scrollIntoView({ behavior: "smooth", block: "start" });
    });

    // Deep link: /blogs/recipe?q=cashmere[&page=2] on the bar section.
    if (!isPage) {
      const params = new URLSearchParams(window.location.search);
      const q = (params.get("q") || "").trim();
      if (q) {
        input.value = q;
        // Deep links land at the top of the blog page, above the fold of the search
        // bar. Ease down to the heading anchor so the query and its results are what
        // the reader sees. rAF lets the browser finish its own scroll restoration first.
        const anchor = document.getElementById("search-recipes") || root;
        const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        requestAnimationFrame(() => {
          anchor.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "start" });
        });
        run(q, parseInt(params.get("page") || "1", 10));
      }
    }
  });
})();
